import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { getStorageSite, updateStorageSite, synchronizeStorageSiteWithLockSystem, sendRoundReport } from 'actions/admin/storageSites';
import { handleResponse } from 'actions/actionHelpers';
import routes from 'routes';
import { useHistory } from 'react-router-dom';
import { storageSiteHasLockCodes, storageGroupHasSynchronizableLockSystem } from 'helpers/StorageSiteHelper';

import Loader from 'common/Loader';
import AdminPage from '../../presentational/AdminPage';
import AdminContentWrapper from '../../presentational/AdminContentWrapper';
import AdminActionWrapper from '../../presentational/AdminActionWrapper';
import EditStorageSiteForm from './EditStorageSiteForm';
import Button from '@material-ui/core/Button';
import GetLockCodesDialog from './GetLockCodesDialog';
import { Link } from 'react-router-dom';

const StorageSite = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const params = useParams();

    const [storageSite, setStorageSite] = useState(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lockCodesDialogOpen, setLockCodesDialogOpen] = useState(false);

    const { storageSiteId } = params;

    const hasLockCodes = storageSite && storageSiteHasLockCodes(storageSite);
    const hasSynchronizableLockSystem = storageSite && storageSite.storageGroups.some(sg => storageGroupHasSynchronizableLockSystem(sg));

    useEffect(() => {
       loadStorageSite();
    }, []);

    const handleOpenLockCodesDialogButtonClick = () => {
        setLockCodesDialogOpen(true);
    };

    const handleLockSystemSynchronizationButtonClick = () => {
        setIsLoading(true);
        dispatch(synchronizeStorageSiteWithLockSystem(storageSiteId))
            .then(handleResponse(
                () => {
                    setIsLoading(false);
                    return { snackbar: { message: 'Storage site was synchronized with lock system.' } };
                },
                () => {
                    setIsLoading(false);
                }
            ));
    };

    const handleSendRoundReportButtonClick = () => {
        setIsLoading(true);
        dispatch(sendRoundReport(storageSiteId))
            .then(handleResponse(
                () => {
                    setIsLoading(false);
                    setStorageSite({ ...storageSite, allowSendRoundReportManually: false });
                    return { snackbar: { message: 'Round report was sent.' } };
                },
                () => {
                    setIsLoading(false);
                }
            ));
    };

    const loadStorageSite = () => {
        setIsLoading(true);
        dispatch(getStorageSite(storageSiteId))
            .then(handleResponse(
                response => {
                    setStorageSite(response.payload);
                    setIsLoading(false);
                },
                () => setIsLoading(false)
            ));
    };

    const createAdminUpdateStorageSiteRequest = formValues => ({
      ownerActorId: formValues.ownerActorId
    });

    const handleSubmit = formValues => {
        setIsSubmitting(true);
        const request = createAdminUpdateStorageSiteRequest(formValues);
        dispatch(updateStorageSite(storageSiteId, request))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.storageSites);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Storage site was successfully saved.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleCancel = () => {
        history.push(routes.admin.storageSites);
    };

    const handleGetLockCodesDialogClose = () => {
        setLockCodesDialogOpen(false);
    };

    const title = `Storage site: ${storageSite?.title ?? 'Loading...'}`;

    return(
        <>
            <AdminPage title={title}>
                {
                    isLoading && <Loader />
                }
                {
                    !isLoading && storageSite && (
                        <AdminContentWrapper type="detail">

                            <EditStorageSiteForm
                                storageSite={storageSite}
                                onSubmit={handleSubmit}
                                onCancel={handleCancel}
                                isSubmitting={isSubmitting}
                            />

                            <AdminActionWrapper>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={routes.admin.storageSiteSubscriptionBookingPriceOverview.replace(':storageSiteId', storageSite.id)}
                                >
                                    Show subscription booking price overview
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={`${routes.admin.createTakeover}?storageSiteId=${storageSite.id}`}
                                >
                                    Create takeover
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    component={Link}
                                    to={routes.admin.createStorageSitePriceAdjustment.replace(':storageSiteId', storageSite.id)}
                                >
                                    Adjust price
                                </Button>
                                {
                                    hasLockCodes &&
                                    (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleOpenLockCodesDialogButtonClick}
                                        >
                                            Get lock codes
                                        </Button>
                                    )
                                }
                                {
                                    hasSynchronizableLockSystem &&
                                    (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleLockSystemSynchronizationButtonClick}
                                        >
                                            Synchronize with lock system
                                        </Button>
                                    )
                                }
                                {
                                    storageSite.allowSendRoundReportManually &&
                                    (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleSendRoundReportButtonClick}
                                        >
                                            Send round report
                                        </Button>
                                    )
                                }
                            </AdminActionWrapper>

                        </AdminContentWrapper>
                    )
                }
                {
                    hasLockCodes &&
                    (
                        <GetLockCodesDialog
                            storageSite={storageSite}
                            open={lockCodesDialogOpen}
                            onClose={handleGetLockCodesDialogClose}
                        />
                    )
                }
            </AdminPage>
        </>
    );
};

export default StorageSite;
